import { VehicleCardStatus } from '@/types/enums';
import { getLanguage } from '@/utils/languageUtils';
import { vehicleCardStatusLabels, labelToVehicleCardStatus } from '@/i18n/labels/vehicleCardLabels';

export interface StatusConfig {
    value: VehicleCardStatus;
    label: string;
    color: 'default' | 'secondary' | 'destructive' | 'outline';
    bgColor?: string;
    textColor?: string;
}

// Status color configurations (labels come from i18n)
const STATUS_COLORS: Record<VehicleCardStatus, Omit<StatusConfig, 'value' | 'label'>> = {
    [VehicleCardStatus.ACTIVE]: {
        color: 'default',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800'
    },
    [VehicleCardStatus.INACTIVE]: {
        color: 'secondary',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800'
    },
    [VehicleCardStatus.COMPLETED]: {
        color: 'outline',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800'
    },
    [VehicleCardStatus.INCOMPLETE]: {
        color: 'destructive',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800'
    }
};

// Generate CAR_CARD_STATUSES from i18n labels + color configs
// Uses current language to determine which labels to use
export const getCarCardStatuses = (): StatusConfig[] => {
    const labels = vehicleCardStatusLabels(getLanguage());
    return Object.values(VehicleCardStatus).map((status) => ({
        value: status,
        label: labels[status],
        ...STATUS_COLORS[status]
    }));
};

export const getStatusConfig = (status: string): StatusConfig => {
    // Normalize status to uppercase (backend sends uppercase: ACTIVE, INACTIVE, etc.)
    const normalizedStatus = status.toUpperCase() as VehicleCardStatus;
    const labels = vehicleCardStatusLabels(getLanguage());
    
    // Check if it's a valid enum value
    if (Object.values(VehicleCardStatus).includes(normalizedStatus)) {
        return {
            value: normalizedStatus,
            label: labels[normalizedStatus],
            ...STATUS_COLORS[normalizedStatus]
        };
    }
    
    // Fallback to ACTIVE if not found
    return {
        value: VehicleCardStatus.ACTIVE,
        label: labels[VehicleCardStatus.ACTIVE],
        ...STATUS_COLORS[VehicleCardStatus.ACTIVE]
    };
};

export const getStatusLabel = (status: string): string => {
    return getStatusConfig(status).label;
};

export const getStatusValue = (label: string): VehicleCardStatus => {
    // Use reverse map from i18n (uses current language)
    const reverseMap = labelToVehicleCardStatus(getLanguage());
    return reverseMap[label] || VehicleCardStatus.ACTIVE;
}; 